import { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { faCopy, faLaptopMobile } from '@fortawesome/pro-solid-svg-icons'
import { useMediaQuery } from '@react-hookz/web'
import { isEmpty } from 'lodash-es'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { DashboardServiceAppDevice } from 'Modules/dashboard/components//DashboardServiceAppDevice/DashboardServiceAppDevice'
import { getNewCaseItems } from 'Modules/dashboard/utils/getNewCaseItems.utils'

import { Card, CardBody, CardHeader, Heading, Text } from 'Components/_theme'
import { CopyClipboardButton } from 'Components/CopyClipboardButton/CopyClipboardButton'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'
import { OrderStatusBadge } from 'Components/OrderStatusBadge/OrderStatusBadge'

import { ServiceAppActionsPopup } from 'Modals/ServiceAppActionsPopup/ServiceAppActionsPopup'

import { useActiveUser, useIsServiceApp, useModal } from 'Hooks'

import { buttonize, formatDate } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import type { getEmployeeDashboard_employee_rentals_rentals } from 'Constants/graphql/queries/employee/__generated__/getEmployeeDashboard'

import type { DeviceTypeEnum } from 'Portal/src/types/deviceTypes'
import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import {
  DashboardDeviceSlideTypeEnum,
  type DashboardDevicesProps,
} from './DashboardDevices.types'
import { DashboardDevicesActions } from './DashboardDevicesActions/DashboardDevicesActions'

export const DashboardDevices = (props: DashboardDevicesProps) => {
  const { caseItems, rentals, renderServiceApp, refetch } = props

  const isServiceApp = useIsServiceApp()
  const { activeEmployee } = useActiveUser()
  const navigate = useNavigate()

  const isDesktop = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.LG})`)

  const { setModal } = useModal()

  const openServiceAppActionsPopup = useCallback(
    (rental: getEmployeeDashboard_employee_rentals_rentals) => {
      setModal({
        component: ServiceAppActionsPopup,
        isOpen: true,
        props: {
          rentalID: rental.id,
        },
        type: 'popup',
      })
    },
    [setModal],
  )

  /**
   * Service App handler
   */
  const deviceClickHandler = (
    _event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>,
    type: DashboardDeviceSlideTypeEnum,
    rental?: getEmployeeDashboard_employee_rentals_rentals,
    orderId?: string,
  ) => {
    if (type === DashboardDeviceSlideTypeEnum.Request) {
      const orderPath = composePath({
        params: {
          employeeId: activeEmployee?.id,
          orderId,
        },
        path: GLOBAL_ROUTE_PATHS.employee.order,
      })

      navigate(orderPath)

      return
    }

    if (rental) {
      openServiceAppActionsPopup(rental)
    }
  }

  const handleOrderClick = (_event: unknown, orderId: string) => {
    const orderPath = composePath({
      params: {
        employeeId: activeEmployee?.id,
        orderId,
      },
      path: GLOBAL_ROUTE_PATHS.employee.order,
    })

    navigate(orderPath)
  }

  const handleRentalClick = (_event: unknown, rentalId: string) => {
    const rentalPath = composePath({
      params: {
        employeeId: activeEmployee?.id,
        rentalId,
      },
      path: GLOBAL_ROUTE_PATHS.employee.rental,
    })

    navigate(rentalPath)
  }

  const newCaseItems = caseItems?.caseItems
    ? getNewCaseItems(caseItems?.caseItems)
    : []

  if (isEmpty(newCaseItems) && isEmpty(rentals)) {
    return null
  }

  const defaultContent = (
    <>
      {!isEmpty(newCaseItems) && (
        <Card className="DashboardDevices mb-6">
          <CardHeader
            className="DashboardDevices-header"
            headingAs={4}
            icon={faLaptopMobile}
          >
            <FormattedMessage id={'My_orders'} />
          </CardHeader>

          <CardBody className="pt-6">
            {newCaseItems?.map(order => (
              <div
                key={order.id}
                className="DashboardDevices-device mb-4 mb-lg-6"
                {...buttonize(handleOrderClick, order.id)}
              >
                <div className="DashboardDevices-device-details d-flex flex-column flex-lg-row position-relative">
                  <div className="m-4 position-absolute top-0 end-0">
                    <OrderStatusBadge status={order.status} />
                  </div>

                  <div className="DashboardDevices-device-image align-self-center">
                    <ImageWithFallback
                      alt={
                        order.data.requestedRentCategory?.deviceDefinition
                          ?.name || 'Device image'
                      }
                      fluid
                      placeholderType={
                        order.data.requestedRentCategory?.deviceDefinition
                          ?.productType as DeviceTypeEnum
                      }
                      src={
                        order.data.requestedRentCategory?.deviceDefinition
                          ?.imageURL || ''
                      }
                    />
                  </div>

                  <div className="w-100">
                    <Heading as={4} className="mb-4 mb-lg-6 me-lg-9" size={3}>
                      {order.data.requestedRentCategory?.deviceDefinition
                        ?.name ?? <FormattedMessage id="Device" />}
                    </Heading>

                    {order.created && (
                      <>
                        <Text className="d-inline-flex w-100 mt-2">
                          <FormattedMessage id="DeviceSlide_request_id" />:{' '}
                          <span className="ms-4 ms-lg-6 me-2 me-lg-4">
                            {order.id}
                          </span>
                        </Text>
                        <Text className="d-inline-flex w-100 mt-2">
                          <FormattedMessage id="DeviceSlide_request_date" />:{' '}
                          <span className="ms-4 ms-lg-6 me-2 me-lg-4">
                            {formatDate(order.created)}
                          </span>
                        </Text>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
      {!isEmpty(rentals) && (
        <Card className="DashboardDevices mb-6">
          <CardHeader
            className="DashboardDevices-header"
            headingAs={4}
            icon={faLaptopMobile}
            subtitle={
              isDesktop ? undefined : (
                <FormattedMessage
                  id="DevicesCard_total"
                  values={{
                    total: rentals?.length,
                  }}
                />
              )
            }
          >
            <FormattedMessage id={'My_devices'} />
            {isDesktop && (
              <span className="ms-4">
                <FormattedMessage
                  id="DevicesCard_total"
                  values={{
                    total: rentals?.length,
                  }}
                />
              </span>
            )}
          </CardHeader>

          <CardBody className="pt-6">
            {rentals?.map(rental => (
              <div
                key={rental.id}
                className="DashboardDevices-device mb-4 mb-lg-6"
              >
                <div className="DashboardDevices-device-details d-flex flex-column flex-lg-row">
                  <div
                    className="DashboardDevices-device-image align-self-center"
                    {...buttonize(handleRentalClick, rental.id)}
                  >
                    <ImageWithFallback
                      alt={rental.deviceDefinition?.name || 'Device image'}
                      fluid
                      placeholderType={
                        rental.deviceDefinition?.productType as DeviceTypeEnum
                      }
                      src={rental.deviceDefinition?.imageURL || ''}
                    />
                  </div>

                  <div className="w-100">
                    <Heading
                      as={4}
                      className="mb-4 mb-lg-6"
                      size={3}
                      {...buttonize(handleRentalClick, rental.id)}
                    >
                      {rental.deviceDefinition?.name ?? (
                        <FormattedMessage id="Device" />
                      )}
                    </Heading>

                    {rental.stocklistItem?.imei && (
                      <span className="w-100 d-inline-flex justify-content-between align-items-center">
                        <Text className="d-inline-flex w-100">
                          <FormattedMessage id="IMEI" />:{' '}
                          <span className="ms-4 ms-lg-6 me-2 me-lg-4">
                            {rental.stocklistItem.imei}
                          </span>
                          {isDesktop && (
                            <CopyClipboardButton
                              icon={faCopy}
                              iconSize={16}
                              value={rental.stocklistItem.imei}
                            />
                          )}
                        </Text>
                        {!isDesktop && (
                          <CopyClipboardButton
                            icon={faCopy}
                            iconSize={16}
                            value={rental.stocklistItem.imei}
                          />
                        )}
                      </span>
                    )}

                    {rental.stocklistItem?.serialNumber && (
                      <span className="w-100 d-inline-flex justify-content-between align-items-center mt-2">
                        <Text className="d-inline-flex w-100">
                          SN:{' '}
                          <span className="ms-4 ms-lg-6 me-2 me-lg-4 text-break">
                            {rental.stocklistItem.serialNumber}
                          </span>
                          {isDesktop && (
                            <CopyClipboardButton
                              icon={faCopy}
                              iconSize={16}
                              value={rental.stocklistItem.serialNumber}
                            />
                          )}
                        </Text>
                        {!isDesktop && (
                          <CopyClipboardButton
                            icon={faCopy}
                            iconSize={16}
                            value={rental.stocklistItem.serialNumber}
                          />
                        )}
                      </span>
                    )}

                    {rental.coPaymentAmount > 0 && (
                      <Text className="d-inline-flex w-100 mt-2">
                        <FormattedMessage id="RentalDetails_copayment" />:{' '}
                        <span className="ms-4 ms-lg-6 me-2 me-lg-4">
                          <FormattedCurrency value={rental.coPaymentAmount} />
                        </span>
                      </Text>
                    )}

                    {rental.newDeviceDate && (
                      <Text className="d-inline-flex w-100 mt-2">
                        <FormattedMessage id="RentalDetails_select_a_new" />:{' '}
                        <span className="ms-4 ms-lg-6 me-2 me-lg-4 align-self-end">
                          {formatDate(rental.newDeviceDate)}
                        </span>
                      </Text>
                    )}
                  </div>
                </div>

                <div className="DashboardDevices-device-actions p-4 p-lg-6">
                  <DashboardDevicesActions refetch={refetch} rental={rental} />
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </>
  )

  const serviceAppContent = (
    <div className="mb-4">
      {newCaseItems?.map(caseItem => (
        <div
          key={caseItem.id}
          {...buttonize(
            deviceClickHandler,
            DashboardDeviceSlideTypeEnum.Request,
            null,
            caseItem.id,
          )}
        >
          <DashboardServiceAppDevice
            created={caseItem.created}
            id={caseItem.id}
            imageURL={
              caseItem.data.requestedRentCategory?.deviceDefinition?.imageURL
            }
            name={
              caseItem.data.requestedRentCategory?.deviceDefinition?.name ??
              'New device'
            }
            productType={
              caseItem.data.requestedRentCategory?.deviceDefinition?.productType
            }
            status={caseItem.status}
            type={DashboardDeviceSlideTypeEnum.Request}
          />
        </div>
      ))}

      {rentals?.map(rental => (
        <div
          key={rental.id}
          {...buttonize(
            deviceClickHandler,
            DashboardDeviceSlideTypeEnum.Rental,
            rental,
          )}
        >
          <DashboardServiceAppDevice
            id={rental?.id}
            imageURL={rental?.deviceDefinition?.imageURL}
            imei={rental?.stocklistItem?.imei}
            isEarlyUpgradable={rental.actions.showEarlyUpgradeAction}
            isUpgradable={
              rental.actions.showStandardUpgradeAction ||
              rental.actions.showDeviceProlongingAction
            }
            name={rental?.deviceDefinition?.name ?? ''}
            productType={rental?.deviceDefinition?.productType}
            startAt={rental.startAt}
            type={DashboardDeviceSlideTypeEnum.Rental}
          />
        </div>
      ))}
    </div>
  )

  return isServiceApp && renderServiceApp ? serviceAppContent : defaultContent
}
